import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSMapContext from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MapboxCamera from 'editor/components/map/MapboxCamera';
import MapboxTool, { MapboxTools } from 'editor/components/map/MapboxTool';
import Markup from 'editor/components/Markup';
import ECSPunch from 'editor/components/PlacePunch';
import noop from 'lodash/noop';
import React, { FC, useContext, useEffect } from 'react';
import { PlacePunch } from 'records/interactions/PlacePunch';
import { MappedSheetVersion } from 'records/MappedSheetVersion';

type Props = {
  interaction: PlacePunch;
};

const ManagePlacePunchInteraction: FC<Props> = (props: Props) => {
  const { interaction } = props;
  const world = useContext(ECSWorldContext);
  const { map: mapComp } = useContext(ECSMapContext);
  const appStore = useContext(AppStoreContext);
  const { state: appState } = appStore;
  const { mapOverlayMachineService } = useMapOverlayMachine();

  const { pageid, sheetid } = interaction;

  useEffect(() => {
    mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);

    world.grabAll(MapboxTool).forEach(({ component }) => {
      component.tool = MapboxTools.PlacePunch;
    });

    world.grabAll(Markup).forEach(({ component }) => {
      component.selected = false;
    });

    // TODO: This "Zoom and Lock" logic is also shared in the ManagePostRFIInteraction component,
    //       and should really be extracted into something common.

    const mappedSheetVersion = appState.mappedSheetVersions.get(
      pageid,
      new MappedSheetVersion()
    );

    const sheetData = mappedSheetVersion.imagesource?.calibrationconfig;
    if (!sheetData) return noop;
    if (!mapComp.map) return noop;

    const { bearing } = sheetData;

    const { bottomright, topleft } = sheetData.corners;

    const topLeftPixels = mapComp.map.project([
      topleft.longitude,
      topleft.latitude,
    ]);

    const bottomRightPixels = mapComp.map.project([
      bottomright.longitude,
      bottomright.latitude,
    ]);

    world.grabAll(MapboxCamera).forEach(({ component }) => {
      component.tracked.screenCoordinates = {
        topLeft: topLeftPixels,
        bottomRight: bottomRightPixels,
        bearing: -bearing,
      };
    });

    const entity = world.createEntity().add(new ECSPunch(sheetid, pageid));

    return () => {
      world.grabAll(MapboxTool).forEach(({ component }) => {
        component.tool = MapboxTools.Pointer;
      });

      world.grabAll(MapboxCamera).forEach(({ component }) => {
        component.tracked.locked = false;
      });

      // there should be no more "new" punches once this unmounts.
      world.grabAll(Markup).forEach(({ component }) => {
        if (component.isNewPunch) {
          component.selected = true;
          component.isNewPunch = false;
        } else {
          component.selected = false;
        }
      });

      entity.destroy();
    };
  }, []);

  return null;
};

const ManagePlacePunchInteractionWrapper: FC = () => {
  const { state: appState } = useContext(AppStoreContext);
  const placePunchInteractions = appState.getInteractions('PlacePunch');

  if (placePunchInteractions.size === 1) {
    return (
      <ManagePlacePunchInteraction
        interaction={placePunchInteractions.get(0, new PlacePunch())}
      />
    );
  }

  return null;
};

export default ManagePlacePunchInteractionWrapper;
